import { Link } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getDraftStepNav } from "@/lib/draft-step-nav";
import { cn } from "@/lib/utils";

/** Optional inner max-width; default is the full content pane. */
export type DraftStepFooterContentWidth = "3xl" | "4xl" | "5xl" | "full";

const CONTENT_WIDTH_CLASS: Record<DraftStepFooterContentWidth, string> = {
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  full: "max-w-none",
};

export interface DraftStepFooterBackAction {
  label: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface DraftStepFooterPrimaryAction {
  label: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  showChevron?: boolean;
}

interface DraftStepFooterProps {
  matterId?: string;
  currentStep: number;
  matterStatus?: string;
  contentWidth?: DraftStepFooterContentWidth;
  className?: string;
  /** When set, overrides route-based back navigation (wizard steps). */
  backAction?: DraftStepFooterBackAction;
  /** When set, overrides route-based continue navigation (wizard steps). */
  primaryAction?: DraftStepFooterPrimaryAction;
}

export function DraftStepFooter({
  matterId,
  currentStep,
  matterStatus,
  contentWidth = "full",
  className,
  backAction,
  primaryAction,
}: DraftStepFooterProps) {
  const routeNav = matterId ? getDraftStepNav(matterId, currentStep, { matterStatus }) : null;

  const resolvedBack: DraftStepFooterBackAction | null = backAction
    ? backAction
    : routeNav?.previousHref && routeNav.previousLabel
      ? { label: routeNav.previousLabel, href: routeNav.previousHref }
      : currentStep === 1
        ? { label: "Back to drafts", href: "/drafting" }
        : null;

  const resolvedPrimary: DraftStepFooterPrimaryAction | null = primaryAction
    ? primaryAction
    : routeNav?.nextHref && routeNav.nextLabel
      ? {
          label: routeNav.nextLabel,
          href: routeNav.nextHref,
          showChevron: true,
        }
      : null;

  return (
    <div
      className={cn(
        "shrink-0 border-t border-[var(--lm-border-subtle)] bg-[var(--bar-bg)] backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-900/80",
        className
      )}
      role="navigation"
      aria-label="Draft step navigation"
    >
      <div className="px-4 py-3 md:px-6">
        <div
          className={cn(
            "mx-auto flex w-full items-center justify-between gap-3",
            CONTENT_WIDTH_CLASS[contentWidth]
          )}
        >
          {resolvedBack ? (
            resolvedBack.href ? (
              <Button variant="outline" asChild disabled={resolvedBack.disabled}>
                <Link to={resolvedBack.href}>{resolvedBack.label}</Link>
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={resolvedBack.disabled}
                onClick={resolvedBack.onClick}
              >
                {resolvedBack.label}
              </Button>
            )
          ) : (
            <span aria-hidden="true" />
          )}

          {resolvedPrimary ? (
            resolvedPrimary.href ? (
              <Button
                variant="default"
                asChild
                className="gap-2"
                disabled={resolvedPrimary.disabled || resolvedPrimary.loading}
              >
                <Link to={resolvedPrimary.href}>
                  {resolvedPrimary.loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  {resolvedPrimary.label}
                  {resolvedPrimary.showChevron ? <ChevronRight className="h-4 w-4" /> : null}
                </Link>
              </Button>
            ) : (
              <Button
                type="button"
                variant="default"
                className="gap-2"
                disabled={resolvedPrimary.disabled || resolvedPrimary.loading}
                onClick={resolvedPrimary.onClick}
              >
                {resolvedPrimary.loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                {resolvedPrimary.label}
                {resolvedPrimary.showChevron ? <ChevronRight className="h-4 w-4" /> : null}
              </Button>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
